import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const parseHash = (hash: string): { access_token: string | null } => {
  const hashParams = new URLSearchParams(hash.substring(1));
  const access_token = hashParams.get('access_token');
  return { access_token };
};

export const setTokens = ({
  access_token,
  refresh_token
}: {
  access_token: string;
  refresh_token: string;
}) => {
  try {
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    // Also store in sessionStorage as backup
    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('refresh_token', refresh_token);
  } catch (error) {
    console.error('Error storing tokens:', error);
  }
};

export const getAccessToken = () => {
  try {
    return (
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('access_token')
    );
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

export const getRefreshToken = () => {
  try {
    return (
      localStorage.getItem('refresh_token') ||
      sessionStorage.getItem('refresh_token')
    );
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

export const clearTokens = () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
};

// Conversion Helpers
export const convertWeight = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  // 1 stone = 6.35029318 kg
  if (isNaN(value)) return 0;
  if (fromUnit === toUnit) return value;

  if (fromUnit === 'kg' && toUnit === 'stones') {
    return value / 6.35029318;
  } else if (fromUnit === 'stones' && toUnit === 'kg') {
    return value * 6.35029318;
  }
  return value;
};

export const convertHeight = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  // 1 foot = 30.48 cm
  if (isNaN(value)) return 0;
  if (fromUnit === toUnit) return value;

  if (fromUnit === 'cm' && toUnit === 'feet') {
    return value / 30.48;
  } else if (fromUnit === 'feet' && toUnit === 'cm') {
    return value * 30.48;
  }
  return value;
};

export const formatHeightInCm = (height: number | null | undefined): string => {
  if (height == null || Number.isNaN(Number(height))) return '-';
  return `${height} cm`;
};

export const downloadFile = (url: string) => {
  const link = document.createElement('a');
  link.href = url!;
  link.download = 'downloaded_image';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatUKDate = (
  isoDateString: string | undefined | null,
  outputFormat = 'dd/MM/yyyy'
): string => {
  if (!isoDateString) return '-';

  try {
    const withZ = isoDateString.endsWith('Z')
      ? isoDateString
      : isoDateString + 'Z';

    return formatInTimeZone(parseISO(withZ), 'Europe/London', outputFormat);
  } catch (err) {
    console.error('formatUKDate error:', err);
    return '-';
  }
};

export const formatTatDuration = (minutes: number | null | undefined): string => {
  if (minutes == null) {
    return '-';
  }

  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = minutes % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (mins > 0 || parts.length === 0) {
    parts.push(`${mins}m`);
  }

  return parts.join(' ');
};

// Helper function to calculate the next value
export const getNextValue = (value: string): number => {
  const num = Number(value);
  if (isNaN(num)) return 0;

  // Check if the number has decimal places
  const hasDecimals = value.includes('.');
  if (hasDecimals) {
    // Get the number of decimal places
    const decimalPlaces = value.split('.')[1].length;
    // Calculate the increment based on decimal places (e.g., 0.001 for 3 decimal places)
    const increment = Math.pow(10, -decimalPlaces);
    // Return the next value with the same number of decimal places
    return Number((num + increment).toFixed(decimalPlaces));
  }
  // For whole numbers, increment by 1
  return num + 1;
};

// Helper function to calculate the previous value
export const getPreviousValue = (value: string): number => {
  const num = Number(value);
  if (isNaN(num)) return 0;

  // Check if the number has decimal places
  const hasDecimals = value.includes('.');
  if (hasDecimals) {
    // Get the number of decimal places
    const decimalPlaces = value.split('.')[1].length;
    // Calculate the decrement based on decimal places (e.g., 0.001 for 3 decimal places)
    const decrement = Math.pow(10, -decimalPlaces);
    // Return the previous value with the same number of decimal places
    return Number((num - decrement).toFixed(decimalPlaces));
  }
  // For whole numbers, decrement by 1
  return num - 1;
};
// Helper function to capitalize the first letter of a string
export const capitalize = (str: string | undefined | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};
